/*LoginPage.js*/
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import useInsightsPrefetch from '../hooks/useInsightsPrefetch';
import { useToast } from '../contexts/ToastContext';
import { tryFlushGuestSaveQueueBeforeAuth } from '../utils/guestOfflineQueue';
import { completeAuthSession } from '../utils/completeAuthSession';
import SocialAuthButtons from '../components/auth/SocialAuthButtons';
import AuthShell from '../components/auth/AuthShell';
import AuthField from '../components/auth/AuthField';
import AuthAlert from '../components/auth/AuthAlert';
import styles from '../components/auth/authUi.module.css';

const LoginPage = ({ setAuth }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { prefetchOnLogin } = useInsightsPrefetch();
  const { showInfo } = useToast();

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setError('');

    try {
      await tryFlushGuestSaveQueueBeforeAuth();
      const res = await api.post('/api/auth/login', { email, password });

      completeAuthSession({
        authResponse: res,
        setAuth,
        navigate,
        location,
        showInfo,
        prefetchOnLogin,
        emailFallback: email,
      });
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      const statusCode = error.response?.status;

      if (!error.response) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
        return;
      }

      if (errorMessage.includes('verify your email') || errorMessage.includes('Please verify')) {
        setError('Please verify your email address before logging in. Check your inbox for a verification code.');
      } else if (errorMessage.includes('uses Google') || errorMessage.includes('uses Apple') || errorMessage.includes('social')) {
        setError(errorMessage);
      } else if (errorMessage.includes('Invalid email or password')) {
        setError('The email or password you entered is incorrect. Please check your credentials and try again.');
      } else if (errorMessage.includes('Account is locked') || errorMessage.includes('Account locked')) {
        if (errorMessage.includes('try again in')) {
          setError(errorMessage);
        } else {
          setError('Your account has been temporarily locked due to too many failed login attempts. Please try again in 15 minutes.');
        }
      } else if (errorMessage.includes('Database service unavailable') || statusCode === 503) {
        setError('Our service is temporarily unavailable. Please try again in a few moments.');
      } else if (statusCode === 500) {
        setError('An unexpected error occurred. Please try again in a moment.');
      } else if (errorMessage) {
        setError(errorMessage);
      } else {
        setError('Unable to sign in. Please check your credentials and try again.');
      }
    }
  };

  return (
    <AuthShell
      title="Sign in"
      footer={
        <>
          New to Velox?{' '}
          <Link to="/register" state={location.state ? { ...location.state } : undefined}>
            Create an account
          </Link>
        </>
      }
      note={
        <>
          <a href="/policy/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          <span aria-hidden="true"> · </span>
          <a href="/policy/terms" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>
        </>
      }
    >
      <AuthAlert>{error}</AuthAlert>

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <AuthField
          id="email"
          name="email"
          label="Email"
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="username email"
          autoFocus
          spellCheck="false"
          autoCorrect="off"
          autoCapitalize="off"
          inputMode="email"
        />

        <AuthField
          id="password"
          name="password"
          label="Password"
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          labelAction={
            <Link className={styles.inlineLink} to="/forgot-password" state={{ fromLoginPage: true }}>
              Forgot password?
            </Link>
          }
        />

        <button className={styles.submit} type="submit" aria-label="Sign in to your account">
          Sign in
        </button>
      </form>

      <SocialAuthButtons
        setAuth={setAuth}
        navigate={navigate}
        location={location}
        showInfo={showInfo}
        prefetchOnLogin={prefetchOnLogin}
        onError={setError}
        mode="signin"
      />
    </AuthShell>
  );
};

export default LoginPage;
